import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, Activity, Zap, TrendingDown, RefreshCw,
  ArrowDownRight, Target, Gauge, AlertTriangle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter, CartesianGrid, LineChart, Line, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

function MetricCard({ label, value, sub, color = 'emerald', icon: Icon }) {
  const colorMap = {
    emerald: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/20',
    blue: 'text-blue-400 bg-blue-400/10 border-blue-500/20',
    amber: 'text-amber-400 bg-amber-400/10 border-amber-500/20',
    cyan: 'text-cyan-400 bg-cyan-400/10 border-cyan-500/20',
    red: 'text-red-400 bg-red-400/10 border-red-500/20',
    purple: 'text-purple-400 bg-purple-400/10 border-purple-500/20',
  };
  const c = colorMap[color] || colorMap.emerald;
  const textColor = c.split(' ')[0];
  return (
    <div className={`bg-[#090B0F] border p-4 ${c.split(' ').slice(2).join(' ')}`} data-testid={`tca-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</span>
        {Icon && <Icon className={`w-3.5 h-3.5 ${textColor}`} />}
      </div>
      <p className={`text-xl font-black font-mono ${textColor}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-1 font-mono">{sub}</p>}
    </div>
  );
}

const chartTooltipStyle = {
  background: '#11141D', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 0, fontFamily: 'monospace', fontSize: 11
};

export default function TCAAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/tca/analytics');
      setData(res.data);
    } catch (err) {
      console.error('Error fetching TCA:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center" data-testid="tca-analytics-page">
        <div className="text-center">
          <BarChart3 className="w-10 h-10 text-blue-400 animate-pulse mx-auto mb-3" />
          <p className="text-slate-400 font-mono text-sm">{t('tca.loading')}</p>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const slippageDist = data?.slippage_distribution || [];
  const fillDist = data?.fill_distribution || [];
  const latencyDist = data?.latency_distribution || [];
  const agentBreakdown = data?.agent_breakdown || [];
  const symbolBreakdown = data?.symbol_breakdown || [];
  const vwapScatter = data?.vwap_scatter || [];

  return (
    <div data-testid="tca-analytics-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#030508]/90 backdrop-blur-xl border-b border-white/[0.08] px-4 md:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            <h1 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('tca.title')}</h1>
            <span className="text-[10px] font-mono text-slate-500 bg-white/5 px-2 py-0.5">{t('tca.subtitle')}</span>
          </div>
          <button onClick={fetchData} className="text-slate-400 hover:text-white transition-colors" data-testid="tca-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <main className="p-4 md:p-6 space-y-6">
        {summary.total_trades === 0 ? (
          <div className="bg-[#090B0F] border border-white/10 p-12 text-center">
            <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-mono text-sm">{t('tca.noData')}</p>
            <p className="text-slate-600 text-xs mt-1">{t('tca.noDataSub')}</p>
          </div>
        ) : (
          <>
            {/* Summary Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="tca-summary-grid">
              <MetricCard label={t('tca.avgSlippage')} value={`${summary.avg_slippage_bps} bps`}
                sub={`P95: ${summary.p95_slippage_bps} bps`} color="amber" icon={ArrowDownRight} />
              <MetricCard label={t('tca.avgFillRate')} value={`${summary.avg_fill_rate_pct}%`}
                sub={`Min: ${summary.min_fill_rate_pct}%`} color="emerald" icon={Target} />
              <MetricCard label={t('tca.avgLatency')} value={`${summary.avg_latency_ms}ms`}
                sub={`P95: ${summary.p95_latency_ms}ms`} color="blue" icon={Gauge} />
              <MetricCard label={t('tca.vwapDiff')} value={`${summary.avg_vwap_diff_bps >= 0 ? '+' : ''}${summary.avg_vwap_diff_bps} bps`}
                sub={t('tca.vsVwap')} color="cyan" icon={Activity} />
              <MetricCard label={t('tca.marketImpact')} value={`${summary.avg_market_impact_bps} bps`}
                sub={t('tca.tradesAnalyzed', { count: summary.total_trades })} color="purple" icon={AlertTriangle} />
            </div>

            {/* Charts Row 1: Slippage + Fill Rate Distributions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="slippage-distribution-chart">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
                  <ArrowDownRight className="w-4 h-4 text-amber-400" /> {t('tca.slippageDist')}
                </h3>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={slippageDist}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="bps" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }}
                        axisLine={false} tickLine={false} label={{ value: 'bps', position: 'right', fill: '#64748b', fontSize: 9 }} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={chartTooltipStyle} formatter={v => [v, 'Trades']} />
                      <Bar dataKey="count" fill="#f59e0b" fillOpacity={0.7} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="fill-rate-distribution-chart">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-400" /> {t('tca.fillRateDist')}
                </h3>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={fillDist}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="pct" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }}
                        axisLine={false} tickLine={false} label={{ value: '%', position: 'right', fill: '#64748b', fontSize: 9 }} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={chartTooltipStyle} formatter={v => [v, 'Trades']} />
                      <Bar dataKey="count" fill="#10b981" fillOpacity={0.7} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Charts Row 2: Latency + VWAP Scatter */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="latency-distribution-chart">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-blue-400" /> {t('tca.latencyDist')}
                </h3>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={latencyDist}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="ms" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }}
                        axisLine={false} tickLine={false} label={{ value: 'ms', position: 'right', fill: '#64748b', fontSize: 9 }} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={chartTooltipStyle} formatter={v => [v, 'Trades']} />
                      <Bar dataKey="count" fill="#3b82f6" fillOpacity={0.7} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="vwap-scatter-chart">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-400" /> VWAP Deviation vs Slippage
                </h3>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="vwap_diff_bps" name="VWAP Diff" unit=" bps"
                        tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="slippage_bps" name="Slippage" unit=" bps"
                        tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={chartTooltipStyle} cursor={{ strokeDasharray: '3 3' }}
                        formatter={(v, name) => [v, name]} />
                      <Scatter data={vwapScatter} fill="#06b6d4" fillOpacity={0.6}>
                        {vwapScatter.map((entry, i) => (
                          <Cell key={i} fill={entry.side === 'buy' ? '#10b981' : '#ef4444'} fillOpacity={0.6} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-4 mt-2 justify-center">
                  <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-emerald-500 rounded-full" /><span className="text-[10px] text-slate-500">{t('common.buy')}</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-red-500 rounded-full" /><span className="text-[10px] text-slate-500">{t('common.sell')}</span></div>
                </div>
              </div>
            </div>

            {/* Agent TCA Breakdown */}
            <div className="bg-[#090B0F] border border-white/10 overflow-hidden" data-testid="agent-tca-table">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-400" /> {t('tca.tcaByAgent')}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-[10px] font-mono text-slate-500 text-left px-4 py-2.5 uppercase">{t('tca.agent')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.trades')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.slippage')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.fillRate')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.latency')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.vwapDiff')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.impact')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">P&L</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {agentBreakdown.map(a => (
                      <tr key={a.agent_id} className="hover:bg-white/[0.02] transition-colors" data-testid={`tca-agent-${a.agent_id}`}>
                        <td className="px-4 py-2.5 text-xs font-mono text-white font-medium">{a.agent_id}</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-slate-400 text-right">{a.trades}</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-right">
                          <span className={a.avg_slippage_bps < 0.2 ? 'text-emerald-400' : a.avg_slippage_bps < 0.3 ? 'text-amber-400' : 'text-red-400'}>
                            {a.avg_slippage_bps} bps
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs font-mono text-right">
                          <span className={a.avg_fill_rate_pct >= 99 ? 'text-emerald-400' : 'text-amber-400'}>
                            {a.avg_fill_rate_pct}%
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs font-mono text-right">
                          <span className={a.avg_latency_ms < 40 ? 'text-emerald-400' : a.avg_latency_ms < 70 ? 'text-amber-400' : 'text-red-400'}>
                            {a.avg_latency_ms}ms
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs font-mono text-right">
                          <span className={Math.abs(a.avg_vwap_diff_bps) < 2 ? 'text-emerald-400' : 'text-amber-400'}>
                            {a.avg_vwap_diff_bps >= 0 ? '+' : ''}{a.avg_vwap_diff_bps} bps
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs font-mono text-right text-blue-400">{a.avg_market_impact_bps} bps</td>
                        <td className={`px-4 py-2.5 text-xs font-mono text-right font-bold ${a.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {a.pnl >= 0 ? '+' : ''}${a.pnl.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Symbol Breakdown */}
            <div className="bg-[#090B0F] border border-white/10 overflow-hidden" data-testid="symbol-tca-table">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-amber-400" /> {t('tca.tcaByInstrument')}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-[10px] font-mono text-slate-500 text-left px-4 py-2.5 uppercase">{t('tca.symbol')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.trades')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.slippage')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.latency')}</th>
                      <th className="text-[10px] font-mono text-slate-500 text-right px-4 py-2.5 uppercase">{t('tca.vwapDiff')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {symbolBreakdown.map(s => (
                      <tr key={s.symbol} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-2.5 text-xs font-mono text-white font-medium">{s.symbol}</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-slate-400 text-right">{s.trades}</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-amber-400 text-right">{s.avg_slippage_bps} bps</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-blue-400 text-right">{s.avg_latency_ms}ms</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-cyan-400 text-right">
                          {s.avg_vwap_diff_bps >= 0 ? '+' : ''}{s.avg_vwap_diff_bps} bps
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
